import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, ExternalLink, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATS = { app: "Uso dell'app", lavoro: "Quesito di lavoro" };

function NewTicket({ onDone }) {
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState({ category: "lavoro", subject: "", message: "", job_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data.slice(0, 50))); }, []);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/support", { ...f, job_id: f.job_id || null }); toast.success("Quesito inviato all'amministratore"); setF({ category: "lavoro", subject: "", message: "", job_id: "" }); onDone(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="support-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Argomento</Label>
          <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="support-category-select">
            {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><Label>Lavoro collegato (opzionale)</Label>
          <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={f.job_id} onChange={(e) => setF({ ...f, job_id: e.target.value })} data-testid="support-job-select">
            <option value="">Nessuno</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.scheduled_date} · {j.client_name}</option>)}
          </select></div>
      </div>
      <div><Label>Oggetto</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="support-subject-input" /></div>
      <div><Label>Descrivi il problema o la domanda</Label><Textarea rows={4} required value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="support-message-input" /></div>
      <Button type="submit" disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="support-submit-button"><Send className="w-4 h-4 mr-2" /> Invia</Button>
    </form>
  );
}

function AdminTicket({ t, onDone }) {
  const [note, setNote] = useState(t.admin_note || "");
  const [busy, setBusy] = useState(false);
  const save = async (status) => {
    try { await api.patch(`/support/${t.id}`, { admin_note: note, ...(status ? { status } : {}) }); toast.success("Salvato"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resum = async () => {
    setBusy(true);
    try { await api.post(`/support/${t.id}/resummarize`); onDone(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <li className="p-5 space-y-3" data-testid={`ticket-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{t.user_name}</span>
        <span className="text-xs text-sky-700">{CATS[t.category]}</span>
        {t.job_label && <span className="text-xs text-slate-500">· {t.job_label}</span>}
        <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${t.status === "risolto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{t.status}</span>
      </div>
      <div className="text-sm font-semibold text-slate-800">{t.subject}</div>
      <p className="text-sm text-slate-600 whitespace-pre-wrap">{t.message}</p>
      <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-sm text-slate-800 whitespace-pre-wrap" data-testid={`ticket-summary-${t.id}`}>
        <div className="flex items-center gap-1 text-xs font-bold text-violet-700 mb-1"><Sparkles className="w-3.5 h-3.5" /> Riassunto di Claude</div>
        {t.ai_status === "in_corso" ? "Claude sta preparando il riassunto…" : t.ai_summary || "Riassunto non disponibile."}
        <button onClick={resum} disabled={busy} className="mt-2 flex items-center gap-1 text-xs text-violet-700 font-semibold" data-testid={`ticket-resummarize-${t.id}`}><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Rigenera</button>
      </div>
      <Textarea rows={2} placeholder="Nota / risposta per il dipendente" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`ticket-note-${t.id}`} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => save()} variant="outline" data-testid={`ticket-save-${t.id}`}>Salva nota</Button>
        {t.status !== "risolto" && <Button size="sm" onClick={() => save("risolto")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ticket-resolve-${t.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Risolto</Button>}
      </div>
    </li>
  );
}

function Digest({ links }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/support/digest"); setText(data.summary); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="support-digest-button">
          <Sparkles className="w-4 h-4 mr-2" /> {busy ? "Claude sta riassumendo…" : "Riassumi quesiti e lavori della settimana"}
        </Button>
        {links.map((u, i) => (
          <a key={u} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid={`artifact-link-${i}`}>
            <ExternalLink className="w-4 h-4" /> Artefatto Claude {i + 1}
          </a>
        ))}
      </div>
      {text && <div className="text-sm text-slate-800 whitespace-pre-wrap bg-violet-50 rounded-xl p-4" data-testid="support-digest-text">{text}</div>}
    </section>
  );
}

export default function Support() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [links, setLinks] = useState([]);
  const load = () => api.get("/support").then((r) => setList(r.data));
  useEffect(() => {
    load();
    if (isAdmin) { api.get("/settings").then((r) => setLinks(r.data.assistant?.artifact_links || [])); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!list.some((t) => t.ai_status === "in_corso")) return;
    const id = setTimeout(load, 4000);
    return () => clearTimeout(id);
  }, [list]);
  return (
    <div className="space-y-6" data-testid="support-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Supporto</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Assistenza</h1>
        <p className="text-slate-500 mt-2 text-sm">{isAdmin ? "Ogni quesito arriva con il riassunto di Claude, anche per email." : "Scrivi un dubbio sull'app o sul lavoro: arriva all'amministratore, che ti risponderà qui."}</p>
      </header>
      {isAdmin ? <Digest links={links} /> : <NewTicket onDone={load} />}
      <ul className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="tickets-list">
        {list.length === 0 && <li className="p-10 text-center text-slate-400">Nessun quesito.</li>}
        {list.map((t) => isAdmin ? <AdminTicket key={t.id} t={t} onDone={load} /> : (
          <li key={t.id} className="p-4" data-testid={`ticket-${t.id}`}>
            <div className="flex items-center justify-between gap-2"><span className="font-semibold text-sm text-slate-900">{t.subject}</span><span className="text-[10px] font-bold uppercase text-slate-500">{t.status}</span></div>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{t.message}</p>
            {t.admin_note && <p className="text-sm text-sky-700 mt-2">Risposta: {t.admin_note}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
